import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from 'primereact/button';
import moment from 'moment';
import { diffChars } from 'diff';
import AudioFileIcon from '../../assets/icons/audio-perrigo.svg';
import VideoFileIcon from '../../assets/icons/video-perrigo.svg';
import ExcelFileIcon from '../../assets/icons/excel-perrigo.svg';
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'aac', 'aiff', 'flac', 'm4a', 'ogg']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'avi', 'mov', 'webm', 'wmv', 'flv']);
const EXCEL_EXTENSIONS = new Set(['xls', 'xlsx']);
// Audio/video/excel files have no renderable thumbnail (server or local) —
// this maps their extension to a dedicated icon instead of the generic file
// icon. Returns null for any other type, so callers fall back to their own default.
export const getFileTypeIconUrl = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() ?? '';
    if (AUDIO_EXTENSIONS.has(ext))
        return AudioFileIcon;
    if (VIDEO_EXTENSIONS.has(ext))
        return VideoFileIcon;
    if (EXCEL_EXTENSIONS.has(ext))
        return ExcelFileIcon;
    return null;
};
export const cssClasses = {
    'cough-cold': 'bright-gray',
    'analgesics': 'lumber',
    'nutritionals-gi': 'light-yellow',
    'allergy': 'aero-blue',
    'derm': 'lavender',
    'default': 'default-folder'
};
export const normalizeCategoryKey = (category) => {
    return category
        .toLowerCase()
        .replace(/[\/&\s]+/g, '-')
        .replace(/-+/g, '-')
        .trim();
};
export const getCssClass = (category) => {
    const key = normalizeCategoryKey(category);
    return cssClasses[key] || cssClasses.default;
};
export const capitalizeWords = (str) => {
    return str?.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};
export const handleError = (error, toast) => {
    if ([401, 404, 500, 502, 504, 0].includes(error?.request?.status)) {
        const toastmsg = error?.response?.data?.message ? error?.response?.data?.message : 'Something is wrong. Please try after sometime.';
        toast.current.show({ severity: 'error', summary: 'Error', detail: toastmsg });
    }
};
export const formatRoleText = (role) => {
    return role
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
export const getInitials = (name) => {
    const parts = name.split(' ');
    const firstInitial = parts[0][0].toUpperCase();
    const lastInitial = parts[parts.length - 1][0].toUpperCase();
    return firstInitial + lastInitial;
};
export const formatDateRange = (range) => {
    const startDate = new Date(Date.UTC(new Date(range.startDate).getFullYear(), new Date(range.startDate).getMonth(), new Date(range.startDate).getDate(), 0, 0, 0, 0));
    let endDate = null;
    if (range.endDate) {
        endDate = new Date(Date.UTC(new Date(range.endDate).getFullYear(), new Date(range.endDate).getMonth(), new Date(range.endDate).getDate(), 23, 59, 59, 999));
    }
    const format = (date) => date.toISOString().split('T')[0];
    return {
        start: format(startDate),
        end: endDate ? format(endDate) : null,
    };
};
export const renderMentionFormattedText = (data, mentions, backgroundColor, originalText = '') => {
    if (!data)
        return '';
    const mentionRegex = /\[@mention\((.*?)\)\]/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const boldTextRegex = /\[\[(.*?)\]\]/g;
    const tempData = typeof data === 'string' ? data : data.text;
    let formattedText = tempData;
    if (originalText) {
        const diff = diffChars(originalText, formattedText);
        formattedText = diff
            .map((part) => {
            if (part.added) {
                return `<span style="color: #DB195A">${part.value}</span>`;
            }
            if (part.removed) {
                return '';
            }
            return part.value;
        })
            .join('');
    }
    formattedText = formattedText?.replace(mentionRegex, (match, key) => {
        let user;
        if (mentions && Object.keys(mentions).length > 0) {
            if (Array.isArray(mentions)) {
                user = mentions.find((u) => u.id === key || u.email === key);
            }
            else {
                user = mentions[key];
            }
        }
        if (user) {
            const fullName = `${user.first_name} ${user.last_name}`;
            return `<span class="user-mention-content" style="background-color: ${backgroundColor || '#e3f2fd'}; padding: 2px 4px; border-radius: 4px;">${fullName}</span>`;
        }
        return `<span class="user-mention-content" style="background-color: ${backgroundColor || '#e3f2fd'}; padding: 2px 4px; border-radius: 4px;">@${key}</span>`;
    });
    formattedText = formattedText?.replace(boldTextRegex, (match, text) => {
        return `<span style="font-weight: bold; font-size: 15px;">${text}</span>`;
    });
    formattedText = formattedText?.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" title="${url}" style="color: blue; text-decoration: underline;">Link</a>`;
    });
    return formattedText;
};
export const renderLinkFormattedText = (text) => {
    if (!text)
        return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    for (const match of text.matchAll(urlRegex)) {
        const start = match.index ?? 0;
        if (start > lastIndex) {
            parts.push(text.slice(lastIndex, start));
        }
        const url = match[0];
        parts.push(_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", style: { color: 'blue', textDecoration: 'underline' }, children: url }, `url-${start}`));
        lastIndex = start + url.length;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return _jsx("div", { children: parts });
};
export const extractFirstMentionedFullName = (data, mentions) => {
    const match = data?.match(/\[@mention\((.*?)\)\]/);
    if (match && mentions[match[1]]) {
        const user = mentions[match[1]];
        return `${user.first_name} ${user.last_name}`;
    }
    return null;
};
export const getMimeType = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const map = {
        mp4: 'video/mp4',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        mov: 'video/quicktime',
        webm: 'video/webm',
        mpeg: 'video/mpeg',
        mpg: 'video/mpeg',
        wmv: 'video/x-ms-wmv',
        flv: 'video/x-flv',
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        aac: 'audio/aac',
        aiff: 'audio/aiff',
        flac: 'audio/flac',
        m4a: 'audio/mp4',
        ogg: 'audio/ogg'
    };
    return map[extension] || 'application/octet-stream';
};
export const base64ToBlobUrl = (base64, mimeType = 'application/octet-stream') => {
    const base64WithoutPrefix = base64.split(',').pop() || '';
    const byteCharacters = atob(base64WithoutPrefix);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
};
export const groupByFirstPermission = (users) => {
    const grouped = users.reduce((acc, userItem) => {
        const permission = userItem?.permissions[0];
        let group = acc.find((g) => g.roleName === permission);
        if (!group) {
            group = { roleName: permission, usergroup_list: [] };
            acc.push(group);
        }
        group.usergroup_list.push(userItem);
        return acc;
    }, []);
    return grouped;
};
export const formatDecimalNumber = (num, decimalNum) => {
    return Number.isInteger(num) ? num : parseFloat(num.toFixed(decimalNum));
};
export const secondsToMinutes = (seconds, decimalPlaces = 2) => {
    const minutes = seconds / 60;
    return formatDecimalNumber(minutes, decimalPlaces);
};
export const formatTime = (timeInSeconds) => {
    if (!Number.isFinite(timeInSeconds) || timeInSeconds < 0) {
        return '0:00.00';
    }
    const totalMs = Math.round(timeInSeconds * 1000);
    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const centiseconds = Math.floor((totalMs % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${centiseconds
        .toString()
        .padStart(2, '0')}`;
};
export const getRelativeTime = (created_datetime) => {
    const now = moment();
    const created = moment(created_datetime);
    const diffMinutes = now.diff(created, 'minutes');
    const diffHours = now.diff(created, 'hours');
    const diffDays = now.diff(created, 'days');
    if (diffMinutes < 1) {
        return 'Just now';
    }
    else if (diffMinutes < 60) {
        return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
    }
    else if (diffHours < 24) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }
    else {
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }
};
export function formatISOToMMDDYYYY(isoString) {
    if (!isoString)
        return '';
    const datePart = isoString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
}
export const customPaginatorTemplate = {
    layout: 'CurrentPageReport PrevPageLink PageLinks NextPageLink',
    CurrentPageReport: (options) => {
        return (_jsxs("div", { className: "flex-1 cus-pager-page-report", children: [options.first, " - ", options.last, " of ", options.totalRecords, " items"] }));
    },
    PrevPageLink: (options) => {
        return (_jsx(Button, { className: "border cus-prev-btn", onClick: options.onClick, disabled: options.disabled, label: "Previous" }));
    },
    NextPageLink: (options) => {
        return (_jsx(Button, { className: "border cus-nxt-btn", onClick: options.onClick, disabled: options.disabled, label: "Next" }));
    },
    PageLinks: (options) => {
        const { page, currentPage, onClick, className, ariaLabel, ariaCurrent } = options;
        const isActive = currentPage === page;
        return (_jsx(Button, { onClick: (e) => onClick(e, page), className: `m-0 border ${isActive ? 'cus-active-page cus-pages' : 'cus-pages'}`, "aria-label": ariaLabel, "aria-current": ariaCurrent, children: page + 1 }, page));
    }
};
export const getStatusInfo = (status, createdBy) => {
    let iconInfo;
    switch (status) {
        case 'new':
            if (createdBy !== 'agency') {
                iconInfo = { statusText: 'Pending Upload' };
            }
            else {
                iconInfo = { statusText: 'New' };
            }
            break;
        case 'materials_uploaded':
            iconInfo = { statusText: 'Materials Uploaded' };
            break;
        case 'asset_upload':
            iconInfo = { statusText: 'Asset Uploaded' };
            break;
        case 'in_review':
            iconInfo = { statusText: 'In Review' };
            break;
        case 'ready_for_meeting':
            iconInfo = { statusText: 'Ready for Meeting' };
            break;
        case 'ready_for_review':
            iconInfo = { statusText: 'Ready for Review' };
            break;
        case 'approved':
            iconInfo = { statusText: 'Approved' };
            break;
        case 'approved_with_changes':
            iconInfo = { statusText: 'Approved with Changes' };
            break;
        case 'needs_revision':
            iconInfo = { statusText: 'Needs Revision' };
            break;
        case 'sent_for_revision':
            iconInfo = { statusText: 'Sent for Revision' };
            break;
        case 'needs_review':
            iconInfo = { statusText: 'Start Review' };
            break;
        case 'cancelled':
            iconInfo = { statusText: 'Cancelled' };
            break;
        case 'complete':
            iconInfo = { statusText: 'Complete' };
            break;
        case 'completed':
            iconInfo = { statusText: 'Complete' };
            break;
        case 'final_asset_uploaded':
            iconInfo = { statusText: 'Complete: Final Asset Uploaded' };
            break;
        case 'asset_uploaded':
            iconInfo = { statusText: 'Asset Uploaded' };
            break;
        case 'sent_for_approval':
            iconInfo = { statusText: 'Sent for Approval' };
            break;
        case 'revise_and_resubmit':
            iconInfo = { statusText: 'Revise & Resubmit' };
            break;
        case 'in_discussion':
            iconInfo = { statusText: 'Needs Meeting' };
            break;
        case 'discuss':
            iconInfo = { statusText: 'Discuss' };
            break;
        case 'accept':
            iconInfo = { statusText: 'Accepted' };
            break;
        case 'ignore':
            iconInfo = { statusText: 'Ignore' };
            break;
        case 'stet':
            iconInfo = { statusText: 'STET' };
            break;
        case 'deleted':
            iconInfo = { statusText: 'Deleted' };
            break;
        case 'ai_action_accept':
            iconInfo = { statusText: 'Added' };
            break;
        case 'ai_action_ignore':
            iconInfo = { statusText: 'Ignored' };
            break;
        case 'ai_study_completed':
            iconInfo = { statusText: 'Completed' };
            break;
        case 'ai_study_job_in_queue':
            iconInfo = { statusText: 'Job in Queue' };
            break;
        case 'ai_study_processing':
            iconInfo = { statusText: 'Processing' };
            break;
        case 'ai_study_failed':
            iconInfo = { statusText: 'Failed' };
            break;
        case 'draft_pending_approval':
            iconInfo = { statusText: 'BO Review' };
            break;
        default:
            iconInfo = { statusText: status };
    }
    return iconInfo;
};
